/**
 * Nayttokerroksen sanasto.
 *
 * Kieli on lukukerros: kannan arvo on `vuokra` tai `alihankinta`, ja
 * ihmisen lukema sana johdetaan siita — ei kovakoodata rinnalle.
 *
 * Tama tiedosto syntyi 29.8.2026 mitatusta viasta. Ruudukossa luki
 * `"vuokrapaikka"` vakiona jokaisella nimeamattomalla rivilla, ja se
 * oli oikein kahdessa instanssissa (protot 12 · demo 10 = `vuokra`)
 * ja vaarin yhdessa: pekilon 41 paikkaa ovat `alihankinta`. Vakion
 * vaihtaminen toiseksi vakioksi olisi vain siirtanyt virheen.
 */
import type { Tyosuhde } from "./shared/types";
import { t } from "./kieli";

/** Nimeamattoman resurssipaikan sana, luettuna tyosuhteesta. Kaannetty 11.10.2026 (englanninkielinen nakyma naytti
 *  suomea: «0 nimeämätöntä paikkaa», kävelyn kielipyyhkäisy protot'ssa ja aaltoflow'ssa). */
export function nimeamattomanSana(ts: Tyosuhde): string {
  return ts === "alihankinta" ? t("sanat.alihankkija")
       : ts === "vuokra"      ? t("sanat.vuokrapaikka")
       :                        t("sanat.nimeamaton");
}

/**
 * "12 vuokrapaikkaa" / "41 alihankkijaa" / "12 vuokrapaikkaa · 3
 * alihankkijaa". Sekalainen joukko luetellaan sellaisenaan eika
 * litisteta yhdeksi sanaksi — juuri se litistys oli vika.
 * Tyhja joukko → tyhja merkkijono (pääChatin toimeksianto Tonin välittämänä 11.10.2026: «kun luku on 0, piilota koko
 * rivi»); kutsuja jattaa osan pois.
 */
export function nimeamattomienSanat(
  rivit: { tyosuhde: Tyosuhde }[]
): string {
  const lajit: Record<string, number> = {};
  for (const r of rivit) {
    const laji = r.tyosuhde === "alihankinta" ? "alihankinta" : r.tyosuhde === "vuokra" ? "vuokra" : "muu";
    lajit[laji] = (lajit[laji] ?? 0) + 1;
  }
  const yksi = { alihankinta: "sanat.alihankkija", vuokra: "sanat.vuokrapaikka", muu: "sanat.nimeamaton" } as const;
  const monta = { alihankinta: "sanat.n_alihankkijaa", vuokra: "sanat.n_vuokrapaikkaa", muu: "sanat.n_nimeamatonta" } as const;
  return Object.entries(lajit)
    .sort((a, b) => b[1] - a[1])
    .map(([l, n]) => n === 1 ? `1 ${t(yksi[l as keyof typeof yksi])}` : t(monta[l as keyof typeof monta], { n }))
    .join(" · ");
}
